import { isBlank, num, type Err } from './common';
import { type Bundle } from './types';

/*
 * Location themes (ADR 0052): a port of shared-cs ContentValidator.Themes.cs with the same messages in the same order. The server runs it
 * last, after the Home partial. `locationThemes[]` (detection order), `locationThemeRules` (absent = the C# defaults: off, no pool) and
 * the `theme:<id>` room templates, including the balance guard (a themed template never allows more zombies or containers than its
 * categories' templates plus shared).
 */

/** Mirrors `LocationThemes.CategoryPrefix`. */
export const THEME_CATEGORY_PREFIX = 'theme:';
/** Mirrors `ContentValidator.MaxThemeIdLength` (`raids.theme` is varchar 32). */
export const MAX_THEME_ID_LENGTH = 32;
const CONTAINER_KINDS = ['cabinet', 'locker', 'register', 'crate', 'shelf'];

/** The theme id inside a `theme:<id>` room-template category, or null. */
export const themeOfCategory = (category: string | null | undefined) =>
  category != null && category.startsWith(THEME_CATEGORY_PREFIX)
    ? category.slice(THEME_CATEGORY_PREFIX.length)
    : null;

/** `theme:<id>` naming a theme of this bundle (ContentValidator.IsThemeCategory). */
export function isThemeCategory(b: Bundle, category: string | null | undefined): boolean {
  const id = themeOfCategory(category);
  return id != null && id !== 'default' && (b.locationThemes ?? []).some((t) => t?.id === id);
}

type Pool =
  | { archetype?: string | null; w?: number | null; types?: (string | null)[] | null }[]
  | null
  | undefined;

export function validateThemes(b: Bundle, err: Err) {
  const themes: { t: NonNullable<NonNullable<Bundle['locationThemes']>[number]>; i: number }[] = [];
  (b.locationThemes ?? []).forEach((t, i) => {
    if (t == null) err('locationThemes', i, '', "Bundle has an empty entry in 'locationThemes'.");
    else themes.push({ t, i });
  });

  const rooms = new Map<string, NonNullable<NonNullable<Bundle['roomTemplates']>[number]>>();
  for (const r of b.roomTemplates ?? [])
    if (r && !isBlank(r.id) && !rooms.has(r.id!)) rooms.set(r.id!, r);
  const categories = new Set((b.categories ?? []).map((c) => c?.id ?? '').filter((x) => x !== ''));
  const zombies = new Set((b.zombies ?? []).map((z) => z?.id ?? '').filter((x) => x !== ''));
  const siteKinds = new Set((b.siteKinds ?? []).map((s) => s?.id ?? '').filter((x) => x !== ''));

  const pool = (
    list: Pool,
    where: string,
    section: 'locationThemes' | 'locationThemeRules',
    index: number | undefined,
    path: string,
  ) =>
    (list ?? []).forEach((z, j) => {
      if (z == null || isBlank(z.archetype)) {
        err(section, index, `${path}.${j}`, `${where} has a zombie look with no archetype.`);
        return;
      }
      if (num(z.w ?? 1) < 1)
        err(
          section,
          index,
          `${path}.${j}.w`,
          `${where} zombie look '${z.archetype}' needs w >= 1.`,
        );
      for (const type of z.types ?? [])
        if (!zombies.has(type ?? ''))
          err(
            section,
            index,
            `${path}.${j}.types`,
            `${where} zombie look '${z.archetype}' uses unknown zombie type '${type}'.`,
          );
    });

  const ids = new Set<string>();
  for (const { t, i } of themes) {
    const id = t.id ?? '';
    if (isBlank(id)) {
      err('locationThemes', i, 'id', 'A location theme has no id.');
      continue;
    }
    if (ids.has(id)) err('locationThemes', i, 'id', `Duplicate location theme id '${id}'.`);
    ids.add(id);
    if (id === 'default')
      err(
        'locationThemes',
        i,
        'id',
        "Location theme id 'default' is reserved (it means no theme).",
      );
    if (!/^[a-z0-9_]+$/.test(id) || id.length > MAX_THEME_ID_LENGTH)
      err(
        'locationThemes',
        i,
        'id',
        `Location theme '${id}' needs an id of lower-case letters, digits and _ (at most ${MAX_THEME_ID_LENGTH}).`,
      );

    const where = `Location theme '${id}'`;
    const own = THEME_CATEGORY_PREFIX + id;
    const cats = t.categories ?? [];
    if (isBlank(t.name)) err('locationThemes', i, 'name', `${where} needs a name.`);
    if (isBlank(t.icon)) err('locationThemes', i, 'icon', `${where} needs an icon.`);
    if (cats.length === 0) err('locationThemes', i, 'categories', `${where} needs categories.`);
    for (const c of cats)
      if (!categories.has(c ?? ''))
        err('locationThemes', i, 'categories', `${where} uses unknown category '${c}'.`);
    for (const s of t.siteKinds ?? [])
      if (siteKinds.size > 0 && !siteKinds.has(s ?? ''))
        err('locationThemes', i, 'siteKinds', `${where} uses unknown site kind '${s}'.`);
    if (t.allCategories && (t.siteKinds ?? []).length === 0)
      err('locationThemes', i, 'allCategories', `${where} sets allCategories without siteKinds.`);
    if (num(t.minAreaM2) < 0)
      err('locationThemes', i, 'minAreaM2', `${where} minAreaM2 is negative.`);
    if (num(t.maxAreaM2) < 0)
      err('locationThemes', i, 'maxAreaM2', `${where} maxAreaM2 is negative.`);
    if (num(t.maxAreaM2) > 0 && num(t.maxAreaM2) < num(t.minAreaM2))
      err('locationThemes', i, 'maxAreaM2', `${where} needs maxAreaM2 >= minAreaM2 (or 0).`);

    const entryId = t.entryTemplate ?? '';
    if (entryId !== '') {
      const entry = rooms.get(entryId);
      if (!entry)
        err(
          'locationThemes',
          i,
          'entryTemplate',
          `${where} entry template '${entryId}' doesn't exist.`,
        );
      else {
        if (!entry.isEntry)
          err(
            'locationThemes',
            i,
            'entryTemplate',
            `${where} entry template '${entryId}' isn't an entry template (isEntry).`,
          );
        if (entry.category !== 'shared' && entry.category !== own)
          err(
            'locationThemes',
            i,
            'entryTemplate',
            `${where} entry template '${entryId}' must be shared or category '${own}'.`,
          );
        if (num(entry.weight ?? 1) <= 0)
          err(
            'locationThemes',
            i,
            'entryTemplate',
            `${where} entry template '${entryId}' needs weight above 0.`,
          );
      }
    } else if (
      ![...rooms.values()].some(
        (r) => r.isEntry && num(r.weight ?? 1) > 0 && (r.category ?? 'shared') === 'shared',
      )
    ) {
      err(
        'locationThemes',
        i,
        'entryTemplate',
        `${where} has no entry template (set entryTemplate or keep a shared entry template).`,
      );
    }

    let small = false;
    for (const rid of t.templates ?? []) {
      const r = rooms.get(rid ?? '');
      if (!r) {
        err('locationThemes', i, 'templates', `${where} lists unknown room template '${rid}'.`);
        continue;
      }
      if (r.isEntry)
        err(
          'locationThemes',
          i,
          'templates',
          `${where} lists entry template '${rid}' among its rooms.`,
        );
      const rc = r.category ?? 'shared';
      if (rc !== own && !cats.includes(rc))
        err(
          'locationThemes',
          i,
          'templates',
          `${where} room template '${rid}' has category '${rc}' (use '${own}' or one of the theme's categories).`,
        );
      small ||= !r.isEntry && num(r.weight ?? 1) > 0 && num(r.minSideM ?? 3) <= 3;
    }
    for (const rid of t.sharedTemplates ?? []) {
      const r = rooms.get(rid ?? '');
      if (!r) {
        err(
          'locationThemes',
          i,
          'sharedTemplates',
          `${where} lists unknown room template '${rid}'.`,
        );
        continue;
      }
      if (r.isEntry)
        err(
          'locationThemes',
          i,
          'sharedTemplates',
          `${where} lists entry template '${rid}' among its rooms.`,
        );
      if ((r.category ?? 'shared') !== 'shared')
        err(
          'locationThemes',
          i,
          'sharedTemplates',
          `${where} shared template '${rid}' isn't shared.`,
        );
      small ||= !r.isEntry && num(r.weight ?? 1) > 0 && num(r.minSideM ?? 3) <= 3;
    }
    if (!small)
      err('locationThemes', i, 'templates', `${where} needs a room template with minSideM <= 3.`);

    if ((t.zombiePool ?? []).length === 0)
      err('locationThemes', i, 'zombiePool', `${where} needs a zombiePool.`);
    pool(t.zombiePool, where, 'locationThemes', i, 'zombiePool');
    for (const e of t.exterior ?? [])
      if (isBlank(e)) err('locationThemes', i, 'exterior', `${where} has an empty exterior entry.`);
  }

  // Every theme:<id> room template: listed by its theme, the five container kinds, and the balance guard.
  (b.roomTemplates ?? []).forEach((r, ri) => {
    const themeId = themeOfCategory(r?.category);
    if (r == null || themeId == null) return;
    const theme = themes.find((x) => x.t.id === themeId)?.t;
    if (!theme) return; // core.ts reports the unknown category
    const where = `Room template '${r.id ?? ''}' (theme '${theme.id}')`;
    const listed = r.id === theme.entryTemplate || (theme.templates ?? []).includes(r.id ?? '');
    if (!listed)
      err(
        'roomTemplates',
        ri,
        'category',
        `${where} isn't in the theme's templates or entryTemplate.`,
      );
    for (const k of r.containerKinds ?? [])
      if (!CONTAINER_KINDS.includes(k ?? ''))
        err('roomTemplates', ri, 'containerKinds', `${where} uses unknown container kind '${k}'.`);
    let maxZombies = 0;
    let maxContainers = 0;
    for (const c of b.roomTemplates ?? []) {
      if (c == null) continue;
      const cc = c.category ?? 'shared';
      if (cc !== 'shared' && !(theme.categories ?? []).includes(cc)) continue;
      maxZombies = Math.max(maxZombies, num(c.zombiesMax));
      maxContainers = Math.max(maxContainers, num(c.containersMax));
    }
    if (num(r.zombiesMax) > maxZombies)
      err(
        'roomTemplates',
        ri,
        'zombiesMax',
        `${where} allows zombiesMax ${num(r.zombiesMax)}, above its categories' ${maxZombies}.`,
      );
    if (num(r.containersMax) > maxContainers)
      err(
        'roomTemplates',
        ri,
        'containersMax',
        `${where} allows containersMax ${num(r.containersMax)}, above its categories' ${maxContainers}.`,
      );
    if (num(r.zombiesMin) > num(r.zombiesMax))
      err('roomTemplates', ri, 'zombiesMin', `${where} needs zombiesMin <= zombiesMax.`);
    if (num(r.containersMin) > num(r.containersMax))
      err('roomTemplates', ri, 'containersMin', `${where} needs containersMin <= containersMax.`);
  });

  const rules = b.locationThemeRules ?? { enabled: false, defaultZombiePool: [] };
  pool(
    rules.defaultZombiePool,
    'Location theme rules default',
    'locationThemeRules',
    undefined,
    'defaultZombiePool',
  );
  if (rules.enabled && (rules.defaultZombiePool ?? []).length === 0)
    err(
      'locationThemeRules',
      undefined,
      'defaultZombiePool',
      'Location theme rules need a defaultZombiePool when enabled.',
    );
}
